/**
 * DriveZone — Subscribe Success Page
 * Route: /subscribe/success  (protected)
 *
 * Reached only when subscription_status === 'active'.
 * Shows M-Pesa receipt, amount paid, and subscription period.
 */

import { useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'

interface SuccessState {
  receipt:    string | null
  period_end: string | null
  amount:     number
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

export default function SubscribeSuccess() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const state     = (location.state as SuccessState) ?? {}

  const receipt   = state.receipt    ?? null
  const periodEnd = state.period_end ?? null

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm card p-8 text-center space-y-5">

        <div className="flex justify-center">
          <CheckCircle className="w-16 h-16 text-emerald-500" />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment successful!</h1>
          <p className="text-sm text-slate-500 mt-2">
            DriveZone Pro is now active on your account.
          </p>
        </div>

        {/* Receipt details */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 text-left">
          {receipt && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">M-Pesa Receipt</span>
              <span className="text-xs font-mono font-semibold text-slate-800">{receipt}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Amount paid</span>
            <span className="text-xs font-semibold text-slate-800">$1.55 USD</span>
          </div>
          {periodEnd && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Valid until</span>
              <span className="text-xs font-semibold text-slate-800">{formatDate(periodEnd)}</span>
            </div>
          )}
        </div>

        <p className="text-sm text-slate-500">You have unlimited searches.</p>

        <button
          onClick={() => navigate('/app/search', { replace: true })}
          className="btn-primary w-full text-base py-3"
        >
          Start searching
        </button>

        <button
          onClick={() => navigate('/account')}
          className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          View account
        </button>
      </div>
    </div>
  )
}
